import type { Schema } from '@dealdesk/api-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FormErrorSummary, TextareaField, applyServerErrors } from '@/business';
import { Button, Dialog, DialogContent, toast } from '@/design-system';
import { DataTable, EmptyState, ErrorPanel, bankColumn, statusColumn, textColumn, type DataColumn } from '@/patterns';
import { useCan } from '@/shared/auth';

import { useUpdateInstitutionAliases } from '../api/mutations';
import { institutionsQuery } from '../api/queries';

type Institution = Schema<'FinancialInstitutionAdmin'>;

/**
 * Setup › Banks & products › Reference institutions (B1): the full bank master behind the liability, retiring bank,
 * settlement and bank relationship selectors — banks, foreign banks and finance companies, partner or not — with the
 * aliases users can search by. Names, codes and partner configuration are not edited here. Inactive institutions
 * (C3: not on the CBUAE register) show the Inactive status: kept for existing records, not offered for new ones.
 */
export function InstitutionsTable() {
  const { t } = useTranslation('banksProducts');
  const can = useCan();
  const result = useQuery(institutionsQuery);
  const [editing, setEditing] = useState<Institution | null>(null);
  const columns = useMemo<DataColumn<Institution>[]>(
    () => [
      bankColumn<Institution>('code', t('institutions.columns.code'), (r) => r.code),
      textColumn<Institution>('name', t('institutions.columns.name'), (r) => r.name, { secondary: (r) => r.nameAr, width: 260 }),
      textColumn<Institution>('kind', t('institutions.columns.kind'), (r) => t(`institutions.kinds.${r.kind}`), { width: 150 }),
      textColumn<Institution>('partner', t('institutions.columns.partner'), (r) => (r.partner ? t('institutions.partner') : '—'), { width: 110 }),
      textColumn<Institution>('aliases', t('institutions.columns.aliases'), (r) => (r.aliases.length ? r.aliases.join(', ') : '—'), { truncate: 360 }),
      // C3: inactive = not offered for new selections (not on the CBUAE register); existing records keep it.
      statusColumn<Institution, 'institutionStatus'>('active', t('institutions.columns.active'), 'institutionStatus', (r) => (r.active ? 'active' : 'inactive'), { width: 110 }),
    ],
    [t],
  );
  if (result.error && !result.data) return <ErrorPanel error={result.error} onRetry={() => void result.refetch()} className="m-4" />;
  return (
    <>
      <DataTable
        label={t('institutions.title')}
        data={result.data}
        columns={columns}
        getRowId={(r) => r.id}
        loading={result.isLoading}
        fetching={result.isFetching}
        {...(can('config.edit') ? { rowActions: (r: Institution) => [{ label: t('institutions.editAliases'), onSelect: () => setEditing(r) }] } : {})}
        empty={<EmptyState title={t('institutions.none')} />}
      />
      {editing ? <AliasesDialog institution={editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

const aliasesSchema = z.object({ aliases: z.string().max(3000, 'validation.server.max_length') });

/** One alias per line (≤30, ≤80 characters each; blanks and duplicates are dropped by the server). 1 field → dialog. */
function AliasesDialog({ institution, onClose }: { institution: Institution; onClose: () => void }) {
  const { t } = useTranslation('banksProducts');
  const save = useUpdateInstitutionAliases();
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<{ aliases: string }>({ resolver: zodResolver(aliasesSchema), defaultValues: { aliases: institution.aliases.join('\n') } });
  const submit = form.handleSubmit(async (v) => {
    setFormError(null);
    try {
      await save.mutateAsync({ bankId: institution.id, version: institution.version, aliases: v.aliases.split('\n').map((a) => a.trim()).filter(Boolean) });
      toast.done(t('institutions.saved', { code: institution.code }));
      onClose();
    } catch (e) {
      const err = applyServerErrors(form, e);
      setFormError(err ? [err.title, ...err.messages].join(' ') : null);
    }
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={t('institutions.aliasesTitle', { code: institution.code })}
        description={t('institutions.aliasesBody')}
        footer={
          <>
            <Button onClick={onClose}>{t('actions.cancel', { ns: 'common' })}</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => void submit()}>
              {t('institutions.save')}
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} noValidate className="flex flex-col gap-4">
          {formError ? <FormErrorSummary title={formError} /> : null}
          <TextareaField control={form.control} name="aliases" label={t('institutions.aliases')} help={t('institutions.aliasesHelp')} rows={6} />
        </form>
      </DialogContent>
    </Dialog>
  );
}
